import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { api } from "@/lib/api-client"
import type { DocumentItem, DocumentFolder, StorageQuota } from "@/lib/types"

// Documents

interface DocumentFilters {
  folder_id?: string
  root?: boolean
  entity_type?: string
  entity_id?: string
}

export function useDocuments(filters: DocumentFilters) {
  return useQuery({
    queryKey: ["documents", filters],
    queryFn: () => {
      const params = new URLSearchParams()
      if (filters.folder_id) params.set("folder_id", filters.folder_id)
      if (filters.root) params.set("root", "true")
      if (filters.entity_type) params.set("entity_type", filters.entity_type)
      if (filters.entity_id) params.set("entity_id", filters.entity_id)
      const qs = params.toString()
      return api.get<{ data: DocumentItem[] }>(`/api/v1/documents${qs ? `?${qs}` : ""}`)
    },
  })
}

export function useUploadDocument() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { file: File; name?: string; folder_id?: string; entity_type?: string; entity_id?: string; description?: string }) => {
      const formData = new FormData()
      formData.append("file", data.file)
      if (data.name) formData.append("name", data.name)
      if (data.folder_id) formData.append("folder_id", data.folder_id)
      if (data.entity_type) formData.append("entity_type", data.entity_type)
      if (data.entity_id) formData.append("entity_id", data.entity_id)
      if (data.description) formData.append("description", data.description)
      return api.upload<{ data: DocumentItem }>("/api/v1/documents", formData)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["documents"] })
      qc.invalidateQueries({ queryKey: ["storage"] })
    },
  })
}

export function useUpdateDocument() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: { name?: string; description?: string; folder_id?: string | null } }) =>
      api.patch<{ data: DocumentItem }>(`/api/v1/documents/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["documents"] })
    },
  })
}

export function useDeleteDocument() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/documents/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["documents"] })
      qc.invalidateQueries({ queryKey: ["storage"] })
    },
  })
}

export function useDocumentDownload() {
  return useMutation({
    mutationFn: (id: string) => api.get<{ data: { url: string } }>(`/api/v1/documents/${id}/download`),
  })
}

// Folders

export function useFolders(parentId?: string) {
  return useQuery({
    queryKey: ["document_folders", parentId ?? "root"],
    queryFn: () => {
      const params = parentId ? `?parent_id=${parentId}` : ""
      return api.get<{ data: DocumentFolder[] }>(`/api/v1/document_folders${params}`)
    },
  })
}

export function useCreateFolder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { name: string; parent_id?: string; color?: string }) =>
      api.post<{ data: DocumentFolder }>("/api/v1/document_folders", { folder: data }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["document_folders"] })
    },
  })
}

export function useDeleteFolder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/document_folders/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["document_folders"] })
    },
  })
}

// Storage

export function useStorageQuota() {
  return useQuery({
    queryKey: ["storage"],
    queryFn: () => api.get<{ data: StorageQuota }>("/api/v1/documents_storage"),
  })
}
